import { useCallback, useEffect, useState } from 'react'
import { myLanes } from '../lib/support'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { supabase } from '../lib/supabase'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { Dialog } from '../components/Dialog'
import { StockRooms } from '../components/shop/StockRooms'
import { ShopItems, NewShopItem } from '../components/shop/ShopItems'
import { RoomStock } from '../components/shop/RoomStock'
import { AddStock } from '../components/shop/AddStock'
import { SHOP_GUIDE, STORE_GUIDE } from '../lib/guides'
import { GuideLink } from '../components/guides/GuideLink'
import { isSectionKey, type SectionKey } from '../lib/shopAdmin'
import { messageOf } from '../lib/errors'
import {
  canReachShopAdmin,
  loadManagedItems,
  loadReorderAlerts,
  loadRoomInventory,
  loadStockRooms,
  type ManagedItem,
  type ReorderAlert,
  type RoomInventoryRow,
  type StockRoom,
} from '../lib/shop'

/**
 * Shop and stock. REC-10's admin screen, and the thing that unblocks B20.
 *
 * ── WHY IT IS ITS OWN ROUTE AND NOT A CARD UNDER People & roles ─────────────
 *
 * The same Administration section, one level down at #/admin/shop, rather than
 * a second card on the people page. The reason is measured rather than
 * aesthetic: that table now holds 932 rows, and anything rendered beneath it
 * sits four hundred rows below the fold.
 *
 * ── THREE SECTIONS, AND STOCK ROOMS IS NO LONGER THE FIRST THING ────────────
 *
 * David, 15 Sep: "Stock rooms is taking up a lot of screen at the top, let's
 * move Stock Rooms to a sub nav." Both cards used to stack, so the sixteen
 * rooms 071 seeded sat above everything — and that is the SAME shape as 072:
 * a screen that was fine with one room and broke when there were sixteen,
 * because a DATA fact moved rather than any code. Sixteen is also not the top
 * of that number; every room that opens makes it worse.
 *
 * The order is the frequency the work happens in. STOCK is counting a shelf,
 * which is most days and is the default. ITEMS is the catalogue — price, what
 * it comes in, pictures, who it is for — which changes when somebody decides
 * something. ROOMS is an address and a manager list, which changes when a room
 * opens.
 *
 * Real ROUTES, not component state: the app's fourth nested section, after
 * #/admin/shop itself and #/stars/store. That buys the Back button, a linkable
 * Rooms tab, and a shelf that is not read for somebody who came to fix a price.
 *
 * ── THE GATE IS NOT admin.roles, AND THAT IS 021'S SPLIT ────────────────────
 *
 * #/admin needs admin.roles. This one needs shop.admin, or managing a room.
 * They are different acts: deciding who may address the whole company is not
 * the same as counting jackets in Burnaby.
 *
 * The second clause is the one that is easy to drop. A room manager holds NO
 * permission at all — 064 made the managers a membership list rather than a
 * scoped grant, because the rooms one person runs need not share a branch of
 * the org tree. So a permission-only check hides this screen from precisely the
 * people it is for. See Me.managesStockRoom.
 *
 * Presentation either way: create_stock_room, set_shop_stock,
 * set_shop_reorder_level and stock_room_inventory each check shop.admin or
 * app.manages_stock_room themselves, and those checks are the boundary. 043's
 * lesson is that a comment claiming so must be true — it is true here, and it
 * was verified function by function rather than assumed.
 *
 * ── THE ITEMS READ IS UNFILTERED, AND THAT IS A DELETION RATHER THAN A BUG ──
 *
 * `shop_manage_items` still takes `p_room`, and nothing passes it any more. The
 * filter was this screen trying to be the room view: an item-first read
 * narrowed to one room, which is the question the Stock tab now answers from a
 * read that starts AT the room. Keeping both would be two answers to "what is
 * on the Burnaby shelf" — and the modals need every room's counts anyway, so a
 * filtered list would have made Add item stock quietly wrong about where things
 * are. The argument for server-side filtering is untouched and still applies
 * the moment something narrows this read again.
 */
/**
 * THE GUIDES LIVE UNDER HELP NOW (339, HLP-45; One and Help v2 S3). Until 31
 * Oct they were this screen's fourth tab (REC-68, 4 Oct). They moved to
 * #/help/guides/store-manager and /shop-admin with every other guide, and the
 * server decides who reads which (app.may_read_guide). "Guide" here is a link
 * there, and each "How … works" link opens its section in the guide sheet
 * beside the page (HLP-49, GuideLink), with a way to the whole guide.
 */
export type ShopTab = 'stock' | 'items' | 'rooms'

export function AdminShop({ me, tab = 'stock' }: { me: Me; tab?: ShopTab }) {
  const [rooms, setRooms] = useState<StockRoom[] | null>(null)
  const [items, setItems] = useState<ManagedItem[] | null>(null)
  // Which shelves are being counted. Route-level, so switching to Items and
  // back does not lose the rooms somebody is standing in. SEVERAL of them as of
  // 075 — a manager with Lima and Santiago counts both in one table.
  const [shelfRoomIds, setShelfRoomIds] = useState<string[]>([])
  /**
   * The shelf and the alerts are loaded HERE rather than inside RoomStock, and
   * that is 062's lesson rather than a preference: effects do not run under
   * `renderToString`, so a component that fetches its own contents is one the
   * suite can only ever see empty — and every assertion written against those
   * contents passes whether they are right or not.
   */
  const [shelf, setShelf] = useState<RoomInventoryRow[] | null>(null)
  const [alerts, setAlerts] = useState<ReorderAlert[]>([])
  const [shelfError, setShelfError] = useState<string | null>(null)
  /**
   * THE ADD-STOCK MODAL HAS ITS OWN ROOM AND ITS OWN SHELF, and that separation
   * is deliberate rather than wasteful. The table now shows SEVERAL rooms and a
   * count belongs to exactly one, so the modal cannot inherit the table's
   * selection; and driving the table from the modal's picker would re-filter
   * the thing you are looking at while you are adding to it. Two questions
   * asked at two moments, so two pieces of state.
   */
  const [addRoomId, setAddRoomId] = useState<string | null>(null)
  // Controlled here rather than inside the modal, so the suite can render the
  // variant grid at all (062). See AddStock's own note.
  const [addItemId, setAddItemId] = useState('')
  const [addShelf, setAddShelf] = useState<RoomInventoryRow[]>([])
  const [addLoading, setAddLoading] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  // One open at a time, held here rather than inside the lists — PeopleTable's
  // shape, and it is what lets the panels be rendered by the test suite.
  const [openRoomId, setOpenRoomId] = useState<string | null>(null)
  /**
   * The open item is the ROUTE (REC-65): #/admin/shop/items/:itemId. Back
   * closes the tray, a link opens it, and `?open=options` is how a just-created
   * item arrives with "What it comes in" expanded. `?item=` on the Stock tab is
   * the tray's "Open on the Stock tab", filtering the shelf to that item.
   */
  const { itemId: routeItemId } = useParams()
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const openItemId = tab === 'items' ? routeItemId ?? null : null
  const openParam = search.get('open')
  const openSections: SectionKey[] | undefined = openParam
    ? openParam.split(',').filter(isSectionKey)
    : undefined
  const stockItemFilter = tab === 'stock' && search.get('item') ? [search.get('item') as string] : []
  function openItem(id: string | null, sections?: SectionKey[]) {
    navigate(id
      ? `/admin/shop/items/${encodeURIComponent(id)}${sections?.length ? `?open=${sections.join(',')}` : ''}`
      : '/admin/shop/items')
  }
  /**
   * TWO MODALS, TWO ACTS — David's separation, and the whole of it. Creating an
   * item is a catalogue decision made once; putting boxes on a shelf happens
   * every time a delivery arrives. They shared one inline form until now, which
   * is why stock could only be typed from inside an expanded item.
   *
   * A modal rather than an inline form because both are a whole task with a
   * beginning and an end, and because the Stock tab's table is the thing being
   * added to: an inline form pushes the table you are checking off the screen.
   * `Dialog` is the app's only surface that traps Tab, locks the page scroll
   * and puts focus back (REC-20); the design system's own Dialog does none of
   * those and is deliberately not used.
   */
  const [creating, setCreating] = useState(false)
  const [addingStock, setAddingStock] = useState(false)
  // Bumped when a modal writes, so the shelf re-reads without the route having
  // to know which room it is showing.
  const [refreshToken, setRefreshToken] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const canShop = canReachShopAdmin(me)
  const canAdmin = can(me, 'shop.admin')
  const tenantId = me.tenants[0]?.id ?? ''


  const refresh = useCallback(async () => {
    if (!canShop) return
    try {
      const [r, i] = await Promise.all([loadStockRooms(), loadManagedItems(null)])
      setRooms(r)
      setItems(i)
      setError(null)
    } catch (e) {
      setError(messageOf(e))
    }
  }, [canShop])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // The room defaults to the only one somebody runs, and to nothing when they
  // run several. 072's finding: auto-selecting worked because there was ONE
  // room, and stopped the day there were sixteen — so the default is a default
  // and never a gate.
  useEffect(() => {
    if (shelfRoomIds.length > 0 || rooms === null) return
    const mine = rooms.filter((r) => r.iManage && r.isActive)
    if (mine.length === 1) {
      setShelfRoomIds([mine[0].id])
      setAddRoomId(mine[0].id)
    }
  }, [rooms, shelfRoomIds])

  // The alert list is asked for on every write, because the whole point of a
  // reorder level is that crossing it surfaces without anybody going looking.
  useEffect(() => {
    if (!canShop) return
    let live = true
    loadReorderAlerts()
      .then((a) => live && setAlerts(a))
      .catch(() => live && setAlerts([]))
    return () => { live = false }
  }, [canShop, refreshToken])

  useEffect(() => {
    // AN EMPTY CHOICE IS ASKED FOR, NOT SKIPPED. 076 makes it mean every room
    // you run, and loadRoomInventory sends it as null. An early return used to
    // sit here and left `shelf` null for good, so RoomStock showed "Reading the
    // shelves…" with no read behind it for anybody running more than one room
    // (David, 4 Oct: "doesn't load"). The one-room default below hid it from
    // anybody running exactly one. DO NOT PUT IT BACK.
    if (!canShop) return
    let live = true
    setShelf(null)
    loadRoomInventory(shelfRoomIds)
      .then((r) => {
        if (!live) return
        setShelf(r)
        setShelfError(null)
      })
      // KEPT, not turned into an empty list. 075 refuses a room you do not run
      // — the whole call, not just that room — and "these shelves are bare" and
      // "you may not read one of them" must not render as the same screen.
      .catch((e) => live && setShelfError(messageOf(e)))
    return () => { live = false }
    // Joined rather than passed as an array: a new array every render would
    // re-fetch on every keystroke elsewhere on the page.
  }, [canShop, shelfRoomIds.join(','), refreshToken])

  // The modal's own shelf, for its prefilled counts and its suggestions.
  useEffect(() => {
    if (!addRoomId) {
      setAddShelf([])
      setAddError(null)
      return
    }
    let live = true
    setAddLoading(true)
    loadRoomInventory([addRoomId])
      .then((r) => {
        if (!live) return
        setAddShelf(r)
        setAddError(null)
      })
      .catch((e) => {
        if (!live) return
        setAddShelf([])
        setAddError(messageOf(e))
      })
      .finally(() => {
        if (live) setAddLoading(false)
      })
    return () => { live = false }
  }, [addRoomId, refreshToken])


  function wrote() {
    setRefreshToken((n) => n + 1)
    void refresh()
  }

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="Shop and stock"
      canAdmin={can(me, 'admin.roles')}
      canShop={canShop}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  if (!canShop) {
    return shell(
      <Card title="Shop and stock">
        <p className="empty">
          This section is for shop administrators and the people who run a stock
          room. If you are expecting to run one, a shop administrator has to add
          you to it.
        </p>
      </Card>,
    )
  }

  const myRoomCount = (rooms ?? []).filter((r) => r.iManage).length

  return shell(
    <div className="admin">
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {/* B20 is the last thing between the shop and opening, and this is the
          screen where it gets answered. Stating it beats leaving somebody to
          wonder why the shop is empty on the day it is meant to open. */}
      {items !== null && items.length === 0 && (
        <p className="admin__notice">
          The shop has nothing in it yet. What is on the shelves on day one, and
          what each thing costs in stars, is a commercial decision rather than a
          technical one — everything needed to record it is here.
        </p>
      )}

      {/* Underlined rather than filled, so it cannot read as a selected chip: a
          chip is a FILTER over a list it narrows, a .modeswitch carries an armed
          claim, and this switches which screen you are on. Three meanings is
          warranted; three looks would not be. Same recipe as #/stars. */}
      <nav className="tabs" aria-label="Shop and stock sections">
        <Link
          to="/admin/shop"
          className={`tab${tab === 'stock' ? ' tab--on' : ''}`}
          aria-current={tab === 'stock' ? 'page' : undefined}
        >
          Stock
        </Link>
        <Link
          to="/admin/shop/items"
          className={`tab${tab === 'items' ? ' tab--on' : ''}`}
          aria-current={tab === 'items' ? 'page' : undefined}
        >
          Items
        </Link>
        <Link
          to="/admin/shop/rooms"
          className={`tab${tab === 'rooms' ? ' tab--on' : ''}`}
          aria-current={tab === 'rooms' ? 'page' : undefined}
        >
          Stock rooms
        </Link>
        {/* A link out to Help ▸ Guides (339), not a tab of this screen. */}
        <Link to={`/help/guides/${STORE_GUIDE}`} className="tab">
          Guide ↗
        </Link>
      </nav>

      {tab === 'stock' && (
        /* NO CARD AROUND THE SHELF (14 Oct). People and roles' shape: the
           count and the two acts on one row, the filters as pills on the next,
           then the table. The card's title said what the tab already says. */
        <section className="roomstock__page" aria-label="What is on the shelf">
          {rooms === null ? (
            <p className="empty" aria-busy="true">
              Loading rooms…
            </p>
          ) : (
            <RoomStock
              key={stockItemFilter.join(',')}
              initialItemFilter={stockItemFilter}
              rooms={rooms}
              roomIds={shelfRoomIds}
              rows={shelf}
              alerts={alerts}
              error={shelfError}
              onPickRooms={setShelfRoomIds}
              onCreateItem={() => setCreating(true)}
              onAddStock={() => {
                // The modal opens on the room you are looking at when that is
                // unambiguous. With several selected it asks, because a count
                // belongs to exactly one shelf and guessing which would put
                // Lima's delivery on Burnaby's.
                if (shelfRoomIds.length === 1) setAddRoomId(shelfRoomIds[0])
                setAddingStock(true)
              }}
              onError={setShelfError}
              onChanged={wrote}
              help={<GuideLink guide={STORE_GUIDE} section="count" label="How counting works" />}
            />
          )}
        </section>
      )}

      {/* ITEMS AND ROOMS IN THE SHELF'S SHAPE (14 Oct): the count on the left,
          the act and its "How … works" at the far end, the list under it — no
          card whose title repeats the tab you just pressed. */}
      {tab === 'items' && (
        <section className="shopadmin__section" aria-label="Items">
          <div className="people__bar">
            <p className="people__count" aria-live="polite">
              {items === null ? 'Loading…' : (
                <strong>{items.length} {items.length === 1 ? 'item' : 'items'}</strong>
              )}
            </p>
            <div className="people__actions">
              {canAdmin
                ? <GuideLink guide={SHOP_GUIDE} section="sections" label="How items work" />
                : <GuideLink guide={STORE_GUIDE} section="local" label="How items work" />}
              {/* At the TOP (REC-65). It sat below the whole list, so adding an
                  item to a catalogue of thirty began with scrolling past it. */}
              <button type="button" className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>
                Add item
              </button>
            </div>
          </div>
          {items === null ? (
            <p className="empty" aria-busy="true">
              Loading items…
            </p>
          ) : (
            <ShopItems
              items={items}
              rooms={rooms ?? []}
              tenantId={tenantId}
              canAdmin={canAdmin}
              openItemId={openItemId}
              onOpenItem={(id) => openItem(id)}
              openSections={openSections}
              onChanged={wrote}
            />
          )}
        </section>
      )}

      {tab === 'rooms' && (
        <section className="shopadmin__section" aria-label="Stock rooms">
          <div className="people__bar">
            <p className="people__count" aria-live="polite">
              {rooms === null ? 'Loading…' : (
                <>
                  <strong>{rooms.length} {rooms.length === 1 ? 'room' : 'rooms'}</strong>
                  {myRoomCount > 0 && <span className="people__countnote"> · you run {myRoomCount}</span>}
                </>
              )}
            </p>
            {canAdmin && (
              <div className="people__actions">
                <GuideLink guide={SHOP_GUIDE} section="rooms" label="How stock rooms work" />
              </div>
            )}
          </div>
          {rooms === null ? (
            <p className="empty" aria-busy="true">
              Loading rooms…
            </p>
          ) : (
            <StockRooms
              rooms={rooms}
              tenantId={tenantId}
              canAdmin={canAdmin}
              openRoomId={openRoomId}
              onOpenRoom={setOpenRoomId}
              onChanged={wrote}
            />
          )}
        </section>
      )}


      {/* Both modals are mounted from the ROUTE rather than from whichever tab
          offered them, so "Create an item" means the same thing on Stock and on
          Items. Two copies of a create form is how a second copy of the thing
          gets published (059's rule for the notice editor). */}
      <Dialog open={creating} title="Add an item" onClose={() => setCreating(false)}>
        <NewShopItem
          tenantId={tenantId}
          rooms={rooms ?? []}
          canAdmin={canAdmin}
          onCreated={(id) => {
            setCreating(false)
            wrote()
            // Straight into the tray with its options open: the item exists
            // before anything hangs off it, which is how the server works.
            openItem(id, ['options'])
          }}
          onCancel={() => setCreating(false)}
        />
      </Dialog>

      <Dialog open={addingStock} title="Add item stock" onClose={() => setAddingStock(false)}>
        <AddStock
          items={items ?? []}
          rooms={rooms ?? []}
          roomId={addRoomId}
          onPickRoom={setAddRoomId}
          itemId={addItemId}
          onPickItem={setAddItemId}
          rows={addShelf}
          loading={addLoading}
          error={addError}
          onError={setAddError}
          onSaved={() => {
            setAddingStock(false)
            wrote()
          }}
          onCancel={() => setAddingStock(false)}
        />
      </Dialog>
    </div>,
  )
}

/** Same rule as the hub's avatar: first letters of the first two words. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}
